/**
 * Unencrypted IndexedDB entry for muted conversation ids.
 *
 * The sealed keystore is great for anything we don't want IDB-at-rest
 * observers to see, but the service worker can't reach through the
 * passphrase-sealed key material — and push handlers are the whole
 * reason "mute" exists in the first place. We keep a parallel, plain-
 * text list of muted conv ids here so sw.js can consult it synchronously
 * on each push and skip notifications for muted rooms.
 *
 * Conv ids are opaque base64url tokens; exposing the set of ids the
 * user has chosen to mute leaks a small amount of metadata (which
 * conversations they're active in) to anything that can read their
 * browser IDB, but that attacker already has direct IndexedDB access
 * and could learn vastly more than this. Worth the tradeoff.
 */
const DB_NAME = 'gamez-settings-v1';
const STORE_NAME = 'kv';
const MUTED_KEY = 'mutedConversations';

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) db.createObjectStore(STORE_NAME);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('idb open failed'));
  });
}

export async function writeMutedConversations(ids: readonly string[]): Promise<void> {
  if (typeof indexedDB === 'undefined') return;
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).put([...ids], MUTED_KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error('idb tx failed'));
    });
    db.close();
  } catch {
    // Best-effort — push-mute is a nicety, not a correctness primitive.
  }
}

export async function readMutedConversations(): Promise<string[]> {
  if (typeof indexedDB === 'undefined') return [];
  try {
    const db = await openDb();
    const list = await new Promise<string[]>((resolve) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const req = tx.objectStore(STORE_NAME).get(MUTED_KEY);
      req.onsuccess = () => {
        const v: unknown = req.result;
        resolve(Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);
      };
      req.onerror = () => resolve([]);
    });
    db.close();
    return list;
  } catch {
    return [];
  }
}
